import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: string;
}

export const Settings: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/v1/auth/me`,
          { withCredentials: true }
        );
        setUser(res.data.data);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/v1/auth/me`,
        { withCredentials: true }
      );
      window.location.href = '/';
    } catch (err: any) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete account. Please contact support.');
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-gray-400 mt-1">Manage your account preferences.</p>
      </div>

      {/* Account section */}
      <div
        className="rounded-xl p-6 mb-6"
        style={{ background: '#111118', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h2 className="text-base font-semibold text-white mb-5">Account</h2>

        {user ? (
          <div className="space-y-5">
            {/* Avatar + name row */}
            <div className="flex items-center gap-4">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0"
                  style={{ background: 'rgba(99,102,241,0.2)', color: '#818cf8' }}
                >
                  {user.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
              <div>
                <div className="text-white font-semibold text-lg">{user.name}</div>
                <div className="text-gray-400 text-sm mt-0.5">{user.email}</div>
                <div
                  className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}
                >
                  {user.role}
                </div>
              </div>
            </div>

            <div className="pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Name</label>
                  <div
                    className="px-3 py-2.5 rounded-lg text-sm text-gray-300"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {user.name}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                  <div
                    className="px-3 py-2.5 rounded-lg text-sm text-gray-300"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {user.email}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Your account details are managed through Google OAuth and cannot be changed here.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-gray-400 text-sm">Unable to load account information.</p>
        )}
      </div>

      {/* Danger zone */}
      <div
        className="rounded-xl p-6"
        style={{ background: '#111118', border: '1px solid rgba(239,68,68,0.2)' }}
      >
        <h2 className="text-base font-semibold text-red-400 mb-2">Danger Zone</h2>
        <p className="text-gray-400 text-sm mb-5">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>

        {deleteError && (
          <div
            className="rounded-lg px-4 py-3 mb-4 text-sm text-red-300"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
          >
            {deleteError}
          </div>
        )}

        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
            style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' }}
          >
            Delete Account
          </button>
        ) : (
          <div
            className="rounded-lg p-4"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
          >
            <p className="text-white font-medium text-sm mb-4">
              Are you absolutely sure? This will permanently delete your account, all agents, and all data.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 disabled:opacity-50"
                style={{ background: '#ef4444', color: '#fff' }}
              >
                {deleteLoading ? 'Deleting...' : 'Yes, delete my account'}
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                style={{ background: 'rgba(255,255,255,0.06)', color: '#d1d5db', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
